import type { VideoData } from "src/components/AssetUploaders/useVideoAssetByMediaId";

import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogClose } from "src/components/ui/dialog";
import { X } from "@phosphor-icons/react";
import { VideoJS } from "src/components/VideoJS";
import assetClient from "src/services/assetClient";

export type VideoDownloadModalProps = {
	video?: VideoData;

	videoUuid?: string | null;

	isOpen: boolean;

	onClose: () => void;
};

const VideoDownloadModal = ({
	isOpen,
	onClose,
	video,
	videoUuid
}: VideoDownloadModalProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [videoSassLink, setVideoSassLink] = useState<string>();

	const playerRef = useRef<any>(null);

	useEffect(() => {
		const fetchSassLink = async () => {
			console.log("Fetching SASS link for video:", videoUuid);

			if (!videoUuid) return;

			setIsLoading(true);
			try {
				const url = `/api/v1.0/asset/video/raw/${videoUuid}`;

				const { data: sassLink } = await assetClient.get<{ url: string }>(url);
				setVideoSassLink(sassLink.url);
			} catch (error) {
				console.error("Error fetching video SASS link:", error);
			}
			setIsLoading(false);
		};

		if (isOpen) {
			fetchSassLink();
		}
	}, [videoUuid, isOpen]);

	const handlePlayerReady = (player: any) => {
		playerRef.current = player;
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-w-[900px] h-[672px] p-0 rounded-xl [&>button:last-child]:hidden">
				<div className="flex justify-end px-3 pt-3">
					<DialogClose className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md border border-border transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
						<X className="h-4 w-4 text-foreground" />
						<span className="sr-only">Close</span>
					</DialogClose>
				</div>
				<div className="bg-muted h-[496px] mx-10">
					{video && (
						<VideoJS
							src={video.playbackUri}
							onReady={handlePlayerReady}
							style={{
								width: video.width,
								maxHeight: 496,
								aspectRatio: video.width / video.height,
								display: "flex",
								justifyContent: "center",
								alignItems: "center"
							}}
						/>
					)}
				</div>

				<div className="flex justify-center mt-10">
					<a
						href={videoSassLink}
						className={[
							"inline-flex items-center px-4 h-9 rounded-lg bg-primary text-white text-sm hover:bg-primary-dark transition-colors",
							isLoading ? "opacity-50 pointer-events-none" : ""
						].join(" ")}
					>
						{isLoading ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
						) : null}
						Download
					</a>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default VideoDownloadModal;
